// src/pages/Intake.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations } from "./landing/landingI18n";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const TRACKS_META = [
  { id: "veterans", key: "veterans", next: "/veterans" },
  { id: "workforce", key: "workforce", next: "/workforce" },
  { id: "economic-opportunity", key: "economicOpportunity", next: "/economic-opportunity" },
  { id: "federal-partners", key: "federalPartners", next: "/federal-partners" },
  { id: "security-compliance", key: "securityCompliance", next: "/security-compliance" },
  { id: "community-impact", key: "communityImpact", next: "/community-impact" },
];

export default function Intake() {
  const q = useQuery();
  const navigate = useNavigate();

  const langParam = (q.get("lang") || "").toLowerCase();
  const savedLang = (() => {
    try {
      return (window.localStorage?.getItem("wow5050_lang") || "").toLowerCase();
    } catch {
      return "";
    }
  })();

  const lang = (["en", "es", "fr"].includes(langParam) ? langParam : ["en", "es", "fr"].includes(savedLang) ? savedLang : "en");
  const t = translations[lang] || translations.en;
  const ti = t?.intakePage || {};

  useEffect(() => {
    try {
      window.localStorage?.setItem("wow5050_lang", lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const s = (key, fallback) => (ti && ti[key] ? ti[key] : fallback);

  const presetTrack = q.get("track") || "";
  const presetNeed = q.get("need") || "";

  const returnTo = (() => {
    const raw = q.get("return") || "/";
    // Only allow in-app relative returns
    return typeof raw === "string" && raw.startsWith("/") ? raw : "/";
  })();

  const tracks = useMemo(() => {
    return TRACKS_META.map((m) => {
      const cfg = t?.programTracks?.[m.key] || {};
      return {
        ...m,
        label: cfg.heading || cfg.trackLabel || m.id,
      };
    });
  }, [t]);

  const [track, setTrack] = useState(presetTrack);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [role, setRole] = useState("individual"); // individual | business | nonprofit | partner
  const [needs, setNeeds] = useState(presetNeed ? [presetNeed] : []);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const trackMeta = tracks.find((x) => x.id === track);

  const needsOptions = ti?.needsOptions || {};
  const needLabel = (id) => {
    const map = {
      employment: needsOptions.employment,
      "skills-training": needsOptions.skillsTraining,
      "business-support": needsOptions.businessSupport,
      "access-to-capital": needsOptions.accessToCapital,
      "federal-market-entry": needsOptions.federalMarketEntry,
      "cmmc-awareness": needsOptions.cmmcAwareness,
      "volunteer-programs": needsOptions.volunteerPrograms,
      partnerships: needsOptions.partnerships,
    };
    return map[id] || id;
  };

  const availableNeeds = useMemo(() => {
    const base = [
      { id: "employment", label: needLabel("employment") },
      { id: "skills-training", label: needLabel("skills-training") },
      { id: "business-support", label: needLabel("business-support") },
      { id: "access-to-capital", label: needLabel("access-to-capital") },
      { id: "federal-market-entry", label: needLabel("federal-market-entry") },
      { id: "cmmc-awareness", label: needLabel("cmmc-awareness") },
      { id: "volunteer-programs", label: needLabel("volunteer-programs") },
      { id: "partnerships", label: needLabel("partnerships") },
    ];

    // If we were deep-linked with a need that isn't in the standard list,
    // include it so it can display as selected.
    if (presetNeed && !base.some((x) => x.id === presetNeed)) {
      base.unshift({ id: presetNeed, label: presetNeed });
    }

    return base;
  }, [lang]); // recompute on language change

  const toggleNeed = (id) => {
    setNeeds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const sendEmail = async (payload) => {
    const res = await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(json?.error || "Request failed");
    return json ?? { ok: true };
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!track) return setStatus({ state: "error", message: s("errProgram", "Please select a program area.") });
    if (!name.trim()) return setStatus({ state: "error", message: s("errName", "Please enter your name.") });
    if (!email.trim()) return setStatus({ state: "error", message: s("errEmail", "Please enter your email.") });

    setStatus({ state: "loading", message: s("submitting", "Submitting…") });

    const selectedTrackLabel = trackMeta?.label || track;
    const selectedNeeds = needs.length ? needs.join(", ") : "(none selected)";

    const subject = `WOW50/50 Intake – ${selectedTrackLabel}`;
    const message =
      `New intake submission\n\n` +
      `Track: ${selectedTrackLabel}\n` +
      `Role: ${role}\n` +
      `Needs: ${selectedNeeds}\n` +
      `Name: ${name}\n` +
      `Email: ${email}\n` +
      `Phone: ${phone || "(not provided)"}\n` +
      `ZIP: ${zip || "(not provided)"}\n\n` +
      `Notes:\n${notes || "(none)"}\n`;

    try {
      await sendEmail({ subject, message, name, email, phone, track, needs, zip, role });

      setStatus({
        state: "success",
        message: s(
          "success",
          "Submitted. Next: use the buttons below to continue to the right place."
        ),
      });
    } catch (err) {
      setStatus({ state: "error", message: err?.message || "Something went wrong." });
    }
  };

  const goToLabel = (program) => {
    const tmpl = s("goTo", "Go to {program}");
    return String(tmpl).replace("{program}", program || "program page");
  };

  return (
    <main className="min-h-screen bg-[#00050f] text-slate-100">
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="rounded-3xl border border-slate-800/70 bg-[#010916]/85 p-6 sm:p-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-emerald-200">
                {s("heading", "Intake Form")}
              </h1>
              <p className="mt-1 text-sm text-slate-300">
                {s(
                  "sub",
                  "Tell us what you need and we’ll route you to the right programs, resources, and next steps."
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(returnTo)}
              className="mt-2 inline-flex items-center justify-center rounded-full border border-slate-700 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10 transition"
            >
              {s("back", "Back")}
            </button>
          </div>

          <form onSubmit={onSubmit} className="mt-6 grid gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("programArea", "Program area")}</span>
                <select
                  value={track}
                  onChange={(e) => setTrack(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">{s("select", "Select…")}</option>
                  {tracks.map((tr) => (
                    <option key={tr.id} value={tr.id}>
                      {tr.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("iAm", "I am a…")}</span>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                >
                  <option value="individual">{ti?.roles?.individual || "Community member / individual"}</option>
                  <option value="business">{ti?.roles?.business || "Business owner"}</option>
                  <option value="nonprofit">{ti?.roles?.nonprofit || "Nonprofit / community org"}</option>
                  <option value="partner">{ti?.roles?.partner || "Employer / partner / agency"}</option>
                </select>
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("fullName", "Full name")}</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                  placeholder={ti?.yourName || "Your name"}
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("email", "Email")}</span>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                  placeholder="you@email.com"
                />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("phoneOptional", "Phone (optional)")}</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                  placeholder="(305) 555-1234"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs font-semibold text-slate-300">{s("zipOptional", "ZIP (optional)")}</span>
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  className="rounded-xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                  placeholder="33196"
                />
              </label>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-[#000914]/70 p-4">
              <div className="text-sm font-semibold text-slate-200">{s("needsTitle", "What do you need help with?")}</div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {availableNeeds.map((n) => (
                  <label key={n.id} className="flex items-start gap-2 rounded-xl border border-slate-800 bg-white/5 p-3 hover:bg-white/10 transition">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={needs.includes(n.id)}
                      onChange={() => toggleNeed(n.id)}
                    />
                    <span className="text-sm text-slate-200">{n.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-400">
                {s("tip", "Tip: you can select multiple — we’ll route you to the right place.")}
              </div>
            </div>

            <label className="grid gap-1">
              <span className="text-xs font-semibold text-slate-300">{s("notesOptional", "Notes (optional)")}</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[110px] rounded-2xl border border-slate-700 bg-[#000914] px-3 py-2 text-sm text-slate-100"
                placeholder={s("notesPlaceholder", "Briefly tell us your goal, timeline, and any constraints.")}
              />
            </label>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="inline-flex items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-500/15 px-5 py-2.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/20 transition disabled:opacity-60"
              >
                {status.state === "loading" ? s("submitting", "Submitting…") : s("submit", "Submit intake")}
              </button>

              {status.message ? (
                <div
                  className={
                    "text-sm " +
                    (status.state === "success"
                      ? "text-emerald-200"
                      : status.state === "error"
                      ? "text-rose-300"
                      : "text-slate-300")
                  }
                >
                  {status.message}
                </div>
              ) : null}
            </div>

            {status.state === "success" ? (
              <div className="mt-2 rounded-2xl border border-slate-800 bg-white/5 p-4">
                <div className="text-sm font-semibold text-slate-200">{s("nextSteps", "Next steps")}</div>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => navigate(returnTo)}
                    className="inline-flex items-center justify-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-200 hover:border-emerald-400 hover:bg-emerald-500/15 transition"
                  >
                    {s("backToStart", "Back to where you started")}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(trackMeta?.next || "/")}
                    className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10 transition"
                  >
                    {goToLabel(trackMeta?.label)}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/outreach")}
                    className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10 transition"
                  >
                    {s("contactUs", "Contact us")}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/volunteer")}
                    className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10 transition"
                  >
                    {s("volunteer", "Volunteer")}
                  </button>
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  {s("review", "We’ll review your intake and follow up with the right program path and official resources.")}
                </div>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </main>
  );
}
